import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Gift, Sparkles } from 'lucide-react';

interface GuessRevealModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function GuessRevealModal({ isOpen, onClose }: GuessRevealModalProps) {
  const [countdown, setCountdown] = useState(15);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setCountdown(15);
      setRevealed(false);
      return;
    }
    setCountdown(15);
    setRevealed(false);
    const id = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          setRevealed(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
        >
          <div className="bg-white rounded-2xl shadow-2xl w-[90%] max-w-5xl min-h-[70vh] overflow-hidden relative flex flex-col">
            <button
              onClick={onClose}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
              aria-label="Fermer"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-pink-600 text-white p-8 flex items-center gap-4">
              <Gift className="w-10 h-10" />
              <div>
                <h3 className="text-3xl font-bold">Mot de la fin : Devinette</h3>
                <p className="text-base text-violet-100">Suspense contrôlé avant la révélation</p>
              </div>
            </div>

            <div className="flex-1 p-8 space-y-8 flex flex-col justify-center">
              {!revealed ? (
                <div className="space-y-6 text-center">
                  <p className="text-xl text-gray-700">Devine en combien de temps cette communication a été bâtie.</p>
                  <p className="text-base text-gray-500">Révélation automatique dans</p>
                  <div className="flex flex-col items-center gap-2">
                    <div className="text-6xl font-extrabold text-violet-600">{countdown}s</div>
                    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden max-w-2xl">
                      <div
                        className="bg-gradient-to-r from-violet-500 to-pink-500 h-2 transition-all"
                        style={{ width: `${((15 - countdown) / 15) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-8 text-center">
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: 'spring', stiffness: 220 }}
                    className="relative inline-block"
                  >
                    <Sparkles className="w-10 h-10 text-amber-400 absolute -top-6 -left-6 animate-pulse" />
                    <Sparkles className="w-10 h-10 text-blue-400 absolute -bottom-6 -right-6 animate-pulse" />
                    <div className="bg-gradient-to-br from-amber-400 via-pink-400 to-violet-500 text-white px-14 py-12 rounded-3xl shadow-2xl border-2 border-white/50 animate-pulse">
                      <div className="text-lg uppercase tracking-wide text-white/80">Réponse</div>
                      <div className="text-7xl font-extrabold drop-shadow-2xl mt-3">15 min</div>
                      <div className="text-base text-white/85 mt-3">Oui, 15 minutes ont suffi !</div>
                    </div>
                  </motion.div>
                  <div className="space-y-1 text-center">
                    <div className="text-xl font-semibold text-gray-800">Gagnant : carte cadeau 500$</div>
                    <div className="text-xl font-semibold text-gray-800">Deuxième : carte cadeau 250$</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

